import React, { useMemo, useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import APIHelper from "../utils/APIHelper";

export default function FinancePage({ token, pageId }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isMock, setIsMock] = useState(false);
  const [dateRange, setDateRange] = useState("30d"); // "30d", "7d", "90d", "custom"
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [balance, setBalance] = useState({ availableBalance: 0, pendingBalance: 0 });
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutModalOpen, setPayoutModalOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [requestingPayout, setRequestingPayout] = useState(false);

  // Mock data for when no real orders exist
  const mockSummary = useMemo(
    () => ({ revenue: 4820, refunds: 240, outstanding: 680, margin: 62 }),
    []
  );

  const mockPayouts = useMemo(
    () => [
      { id: "PAY-001", amount: 1200, status: "Completed", date: "2025-09-01" },
      { id: "PAY-002", amount: 980, status: "Pending", date: "2025-09-03" },
      { id: "PAY-003", amount: 1450, status: "Completed", date: "2025-09-05" },
    ],
    []
  );

  const fetchOrders = async () => {
    try {
      setLoading(true);
      // Fetch all orders (we'll filter by status client-side)
      const response = await APIHelper.listOrders(token, {
        pageId,
        page: 1,
        limit: 1000, // Get all orders to calculate metrics
      });
      const fetchedOrders = response.orders || [];
      setOrders(fetchedOrders);
      setIsMock(fetchedOrders.length === 0);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setIsMock(true); // Show mock data on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && pageId) {
      fetchOrders();
      fetchBalance();
      fetchPayoutHistory();
    } else {
      // If no token/pageId, show mock data
      setIsMock(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pageId]);

  const fetchBalance = async () => {
    if (!token) return;
    try {
      setBalanceLoading(true);
      const response = await APIHelper.getBalance(token);
      setBalance({
        availableBalance: response.availableBalance || 0,
        pendingBalance: response.pendingBalance || 0,
      });
    } catch (error) {
      console.error("Error fetching balance:", error);
    } finally {
      setBalanceLoading(false);
    }
  };

  const fetchPayoutHistory = async () => {
    if (!token) return;
    try {
      setPayoutsLoading(true);
      const response = await APIHelper.getPayoutHistory(token);
      setPayouts(response.payouts || []);
    } catch (error) {
      console.error("Error fetching payout history:", error);
    } finally {
      setPayoutsLoading(false);
    }
  };

  // Update date range when preset is selected
  useEffect(() => {
    const now = new Date();
    switch (dateRange) {
      case "7d":
        setStartDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
        setEndDate(now);
        break;
      case "30d":
        setStartDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
        setEndDate(now);
        break;
      case "90d":
        setStartDate(new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000));
        setEndDate(now);
        break;
      case "custom":
        // Keep current dates, user will select manually
        break;
      default:
        setStartDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
        setEndDate(now);
    }
  }, [dateRange]);

  // Calculate finance metrics from real orders
  const summary = useMemo(() => {
    if (isMock || orders.length === 0) {
      return mockSummary;
    }

    try {
      // Use selected date range
      const rangeStart = new Date(startDate);
      rangeStart.setHours(0, 0, 0, 0);
      const rangeEnd = new Date(endDate);
      rangeEnd.setHours(23, 59, 59, 999);

      // Revenue (30d): Sum of paid orders in last 30 days
      // Include orders with status: paid, processing, ready_for_shipping, shipping_label_generated, shipped, fulfilled
      const paidStatuses = [
        "paid",
        "processing",
        "ready_for_shipping",
        "shipping_label_generated",
        "shipped",
        "fulfilled",
      ];
      const revenueOrders = orders.filter((order) => {
        if (!order || !order.status) return false;
        try {
          const orderDate = order.paymentDate
            ? new Date(order.paymentDate)
            : order.createdAt
            ? new Date(order.createdAt)
            : null;
          if (!orderDate || isNaN(orderDate.getTime())) return false;
          return (
            paidStatuses.includes(order.status) &&
            orderDate >= rangeStart &&
            orderDate <= rangeEnd
          );
        } catch (e) {
          return false;
        }
      });
      const revenue =
        revenueOrders.reduce((sum, order) => {
          try {
            // Amount is stored in paise, convert to rupees
            const amountInRupees =
              order.amountInRupees ||
              (order.amount ? order.amount / 100 : 0) ||
              0;
            return sum + (Number(amountInRupees) || 0);
          } catch (e) {
            return sum;
          }
        }, 0) || 0;

      // Refunds (30d): Sum of cancelled orders in last 30 days
      const refundOrders = orders.filter((order) => {
        if (!order || order.status !== "cancelled") return false;
        try {
          const orderDate = order.cancelledAt
            ? new Date(order.cancelledAt)
            : order.paymentDate
            ? new Date(order.paymentDate)
            : order.createdAt
            ? new Date(order.createdAt)
            : null;
          if (!orderDate || isNaN(orderDate.getTime())) return false;
          return orderDate >= rangeStart && orderDate <= rangeEnd;
        } catch (e) {
          return false;
        }
      });
      const refunds =
        refundOrders.reduce((sum, order) => {
          try {
            const amountInRupees =
              order.amountInRupees ||
              (order.amount ? order.amount / 100 : 0) ||
              0;
            return sum + (Number(amountInRupees) || 0);
          } catch (e) {
            return sum;
          }
        }, 0) || 0;

      // Outstanding: Sum of pending orders
      const outstandingOrders = orders.filter(
        (order) => order && order.status === "pending"
      );
      const outstanding =
        outstandingOrders.reduce((sum, order) => {
          try {
            const amountInRupees =
              order.amountInRupees ||
              (order.amount ? order.amount / 100 : 0) ||
              0;
            return sum + (Number(amountInRupees) || 0);
          } catch (e) {
            return sum;
          }
        }, 0) || 0;

      // Margin: Calculate as (revenue - refunds) / revenue * 100, or use a default
      // For now, we'll use a simple calculation or default to 62%
      const margin =
        revenue > 0
          ? Math.round(((revenue - refunds) / revenue) * 100)
          : 62;

      return { revenue, refunds, outstanding, margin };
    } catch (error) {
      console.error("Error calculating finance summary:", error);
      return mockSummary;
    }
  }, [orders, isMock, mockSummary, startDate, endDate]);

  // Use real payouts if available, otherwise mock
  const displayPayouts = useMemo(() => {
    if (payouts.length > 0) {
      return payouts;
    }
    if (isMock) {
      return mockPayouts;
    }
    return [];
  }, [payouts, isMock, mockPayouts]);

  const statusChip = (s) => {
    if (!s) return null;
    const statusMap = {
      completed: { label: "Completed", color: "success" },
      processing: { label: "Processing", color: "info" },
      pending: { label: "Pending", color: "warning" },
      failed: { label: "Failed", color: "error" },
      Completed: { label: "Completed", color: "success" },
      Pending: { label: "Pending", color: "warning" },
    };
    const config = statusMap[s] || { label: s, color: "default" };
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  // Ensure summary values are numbers
  const safeSummary = useMemo(() => {
    try {
      return {
        revenue: Number(summary?.revenue) || 0,
        refunds: Number(summary?.refunds) || 0,
        outstanding: Number(summary?.outstanding) || 0,
        margin: Number(summary?.margin) || 62,
      };
    } catch (error) {
      console.error("Error creating safe summary:", error);
      return {
        revenue: 0,
        refunds: 0,
        outstanding: 0,
        margin: 62,
      };
    }
  }, [summary]);

  const getRangeLabel = () => {
    switch (dateRange) {
      case "7d":
        return "7d";
      case "30d":
        return "30d";
      case "90d":
        return "90d";
      case "custom":
        return "Custom";
      default:
        return "30d";
    }
  };

  const handleRequestPayout = async () => {
    // Check if bank details exist
    try {
      const bankDetailsResponse = await APIHelper.getBankDetails(token);
      if (!bankDetailsResponse.bankDetails?.accountNumber) {
        toast.error("Please add bank details in Settings first");
        navigate("/dashboard/settings");
        return;
      }
      setPayoutModalOpen(true);
    } catch (error) {
      console.error("Error checking bank details:", error);
      toast.error("Failed to check bank details");
    }
  };

  const handlePayoutSubmit = async () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount < 5000) {
      toast.error("Minimum payout amount is ₹5,000");
      return;
    }

    const availableInRupees = balance.availableBalance / 100;
    if (amount > availableInRupees) {
      toast.error(`Insufficient balance. Available: ₹${availableInRupees.toFixed(2)}`);
      return;
    }

    try {
      setRequestingPayout(true);
      await APIHelper.requestPayout(token, amount);
      toast.success("Payout request submitted successfully");
      setPayoutModalOpen(false);
      setPayoutAmount("");
      fetchBalance();
      fetchPayoutHistory();
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to request payout"
      );
    } finally {
      setRequestingPayout(false);
    }
  };

  const handleRetryPayout = async (requestId) => {
    try {
      await APIHelper.retryPayout(token, requestId);
      toast.success("Payout retry initiated");
      fetchPayoutHistory();
      fetchBalance();
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Failed to retry payout"
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <div className={`flex items-center justify-between ${isMock ? "mb-0" : "mb-2"}`}>
          <div className="flex items-center gap-2">
            <AccountBalanceIcon className="text-primary-600 dark:text-primary-400" />
            <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
              Finance
            </Typography>
          </div>
          <Button
            variant="contained"
            startIcon={<RequestQuoteIcon />}
            onClick={handleRequestPayout}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            Request Payout
          </Button>
        </div>

        {isMock && (
          <div className="mt-4 mb-4 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
            <Typography className="text-sm text-primary-800 dark:text-primary-200">
              ℹ️ Showing mock financial data as placeholders. This will update with real
              data once you have paid orders.
            </Typography>
          </div>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress size={20} />
          </Box>
        )}

        {/* Date Range Selector */}
        <div className="card-modern p-4 mt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-3">
              <FormControl fullWidth size="small">
                <InputLabel className="text-gray-700 dark:text-gray-300">Date Range</InputLabel>
                <Select
                  value={dateRange}
                  label="Date Range"
                  onChange={(e) => setDateRange(e.target.value)}
                  className="bg-white dark:bg-gray-800"
                >
                  <MenuItem value="7d">Last 7 days</MenuItem>
                  <MenuItem value="30d">Last 30 days</MenuItem>
                  <MenuItem value="90d">Last 90 days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </div>
            {dateRange === "custom" && (
              <>
                <div className="md:col-span-4">
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => {
                      if (newValue) setStartDate(newValue);
                    }}
                    maxDate={endDate}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        size="small" 
                        fullWidth 
                        className="bg-white dark:bg-gray-800"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.75rem',
                          },
                        }}
                      />
                    )}
                  />
                </div>
                <div className="md:col-span-4">
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => {
                      if (newValue) setEndDate(newValue);
                    }}
                    minDate={startDate}
                    maxDate={new Date()}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        size="small" 
                        fullWidth 
                        className="bg-white dark:bg-gray-800"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '0.75rem',
                          },
                        }}
                      />
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Balance widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div className="card-modern p-6">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Available Balance
            </Typography>
            {balanceLoading ? (
              <CircularProgress size={24} className="mt-2" />
            ) : (
              <>
                <Typography variant="h5" className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  ₹{(balance.availableBalance / 100).toFixed(2)}
                </Typography>
                <Typography variant="caption" className="text-xs text-gray-500 dark:text-gray-400">
                  Eligible for payout (≥30 days old)
                </Typography>
              </>
            )}
          </div>
          <div className="card-modern p-6">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Pending Balance
            </Typography>
            {balanceLoading ? (
              <CircularProgress size={24} className="mt-2" />
            ) : (
              <>
                <Typography variant="h5" className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  ₹{(balance.pendingBalance / 100).toFixed(2)}
                </Typography>
                <Typography variant="caption" className="text-xs text-gray-500 dark:text-gray-400">
                  Will be available after 30 days
                </Typography>
              </>
            )}
          </div>
        </div>

        {/* Summary widgets */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="card-modern p-6">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Revenue ({getRangeLabel()})
            </Typography>
            <Typography variant="h5" className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              ₹{safeSummary.revenue.toFixed(0)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (safeSummary.revenue / 5000) * 100)}
              className="mt-2"
            />
          </div>
          <div className="card-modern p-6">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Refunds ({getRangeLabel()})
            </Typography>
            <Typography variant="h5" className="text-2xl font-bold text-gray-900 dark:text-white">
              ₹{safeSummary.refunds.toFixed(0)}
            </Typography>
          </div>
          <div className="card-modern p-6">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Outstanding
            </Typography>
            <Typography variant="h5" className="text-2xl font-bold text-gray-900 dark:text-white">
              ₹{safeSummary.outstanding.toFixed(0)}
            </Typography>
          </div>
          <div className="card-modern p-6">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Margin
            </Typography>
            <Typography variant="h5" className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {safeSummary.margin}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, safeSummary.margin))}
              className="mt-2"
            />
          </div>
        </div>

      {/* Payouts table */}
      <div className="card-modern p-4 mt-6">
        <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white mb-4">
          Payouts
        </Typography>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">ID</TableCell>
              <TableCell align="right" className="text-gray-700 dark:text-gray-300 font-semibold">Amount</TableCell>
              <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Status</TableCell>
              <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Date</TableCell>
              <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payoutsLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : displayPayouts.length > 0 ? (
              displayPayouts.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell className="text-gray-900 dark:text-white">
                    {p.orderIds && p.orderIds.length > 0 ? (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                              Orders included:
                            </Typography>
                            {p.orderIds.slice(0, 5).map((oid) => (
                              <Typography key={oid} variant="caption" display="block">
                                {oid}
                              </Typography>
                            ))}
                            {p.orderIds.length > 5 && (
                              <Typography variant="caption" display="block">
                                +{p.orderIds.length - 5} more
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        <Typography variant="body2" sx={{ cursor: "help" }}>
                          {p.id}
                        </Typography>
                      </Tooltip>
                    ) : (
                      p.id
                    )}
                  </TableCell>
                  <TableCell align="right" className="text-gray-900 dark:text-white font-medium">₹{p.amount.toFixed(2)}</TableCell>
                  <TableCell>{statusChip(p.status)}</TableCell>
                  <TableCell className="text-gray-700 dark:text-gray-300">{formatDate(p.date || p.requestedAt)}</TableCell>
                  <TableCell>
                    {p.status === "failed" && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleRetryPayout(p.id)}
                        className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case"
                      >
                        Retry
                      </Button>
                    )}
                    {p.failureReason && (
                      <Typography variant="caption" className="text-red-600 dark:text-red-400 block">
                        {p.failureReason}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" className="text-gray-500 dark:text-gray-400 py-4">
                    No payouts data available yet
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Request Payout Modal */}
      <Dialog
        open={payoutModalOpen}
        onClose={() => {
          setPayoutModalOpen(false);
          setPayoutAmount("");
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: "bg-white dark:bg-gray-900 rounded-2xl shadow-soft-lg border border-gray-100 dark:border-gray-700"
        }}
      >
        <DialogTitle className="text-2xl font-bold text-gray-900 dark:text-white pb-3 pt-6 px-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <span className="text-xl">💰</span>
            </div>
            <span>Request Payout</span>
          </div>
        </DialogTitle>
        <DialogContent className="px-6 py-6">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <Typography className="text-sm text-blue-800 dark:text-blue-200 mb-1">
                <strong>Available balance:</strong> ₹{(balance.availableBalance / 100).toFixed(2)}
              </Typography>
              <Typography className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Minimum payout:</strong> ₹5,000
              </Typography>
            </div>
            <TextField
              label="Amount (₹)"
              type="number"
              value={payoutAmount}
              onChange={(e) => setPayoutAmount(e.target.value)}
              fullWidth
              inputProps={{ min: 5000, step: 1 }}
              helperText="Enter amount between ₹5,000 and available balance"
              className="bg-white dark:bg-gray-800"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.75rem',
                },
              }}
            />
            {payoutAmount && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <Typography variant="body2" className="text-gray-700 dark:text-gray-300">
                  <strong>You will receive:</strong> ₹{parseFloat(payoutAmount || 0).toFixed(2)}
                </Typography>
              </div>
            )}
          </Box>
        </DialogContent>
        <DialogActions className="px-6 pb-6 pt-2">
          <Button
            onClick={() => {
              setPayoutModalOpen(false);
              setPayoutAmount("");
            }}
            disabled={requestingPayout}
            className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 normal-case px-4"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayoutSubmit}
            variant="contained"
            disabled={requestingPayout || !payoutAmount || parseFloat(payoutAmount) < 5000}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            {requestingPayout ? "Processing..." : "Request Payout"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </LocalizationProvider>
  );
}
